import { RowSelectionState } from "@tanstack/react-table";
import map from "lodash/map";
import pick from "lodash/pick";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import EchartsWrapper from "PFCore/helpers/echarts_wrapper";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { Profile, ProfileCustomValue } from "PFCore/types";
import fontFamilies from "PFTheme/tokens/fontFamilies";
import { useCallback, useMemo } from "react";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";

import { SkillsTable } from "../common/skills_table";
import { calculateEChartsTooltipPosition } from "./calculate_echarts_tooltip_position";
import { CategoryTooltip } from "./category_tooltip";
import { getDataFromPath, Node, ratio } from "./helpers";
import { getProficiencyIcon, removeChildrenFromLayer } from "./helpers";
import { useFontExperienceIcons } from "./hooks/use_font_experience_icons";
import css from "./skills_explorer.module.scss";

type SkillsDrilldownProps = {
  skillsTree: Node[];
  currentPath: number[];
  setCurrentPath: (path: number[]) => void;
  height: number;
  width: number;
  profile: Profile;
  showTooltip?: boolean;
  canEditSkills?: boolean;
  rowSelectionState?: RowSelectionState;
  setRowSelectionState?: (state: RowSelectionState) => void;
  maxNameLength?: number;
  allowSkillPreview?: boolean;
};

export const SkillsDrilldown = ({
  currentPath,
  skillsTree,
  setCurrentPath,
  height,
  width,
  profile,
  showTooltip = true,
  canEditSkills,
  rowSelectionState,
  setRowSelectionState,
  maxNameLength,
  allowSkillPreview
}: SkillsDrilldownProps) => {
  const { data: currentAccount } = useCurrentAccount();
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();
  const { experienceToFontIconMap, experienceFontName } = useFontExperienceIcons();

  const { t } = useTranslation("profiles", { keyPrefix: "show.parts" });
  // The visible data which we will actually use for display purposes.
  const visibleData = useMemo(
    () => removeChildrenFromLayer(getDataFromPath(skillsTree, [...currentPath])),
    [currentPath, skillsTree]
  );

  const onChartClick = useCallback(
    ({ dataIndex }) => {
      if (currentPath.length < 2) {
        const newPath = [...currentPath, dataIndex - 1];
        setCurrentPath([...newPath]);
      }
    },
    [currentPath, visibleData]
  );

  const categorySkills = useMemo(() => visibleData.map(({ data }) => data), [visibleData]);

  return (
    <>
      {!!visibleData.length && (
        <EchartsWrapper
          minWidth={300}
          height={height}
          width={width}
          onClick={onChartClick}
          hidden={currentPath.length > 1}
          options={{
            // @ts-ignore
            tooltip: {
              backgroundColor: "transparent",
              padding: 0,
              extraCssText: "",
              position: calculateEChartsTooltipPosition,
              appendToBody: true, //this fix the overflow: hidden isue and lets render outside the box
              formatter: ({ data, color }) =>
                renderToString(
                  <CategoryTooltip
                    data={data}
                    color={color}
                    currentAccount={currentAccount}
                    experienceLevels={experienceLevels}
                    experienceLabelMap={experienceLabelMap}
                  />
                ),
              show: showTooltip
            },
            series: [
              {
                width: "100%",
                height: "100%",
                name: t("charts.profileSkills"),
                top: 0,
                type: "treemap",
                id: "profile_skills_treemap",
                animationDurationUpdate: 800,
                roam: false,
                nodeClick: false,
                data: visibleData.map((item) => {
                  const min = Math.min(...visibleData.map(({ value }) => value || 1));
                  const max = Math.max(...visibleData.map(({ value }) => value || 1));
                  const calculatedRatio = ratio(item.value || 0, min, max);
                  return {
                    ...item,
                    label: {
                      padding: [-12, 0, 0, 0], // To achieve vertical align
                      align: "center",
                      verticalAlign: "top",
                      formatter: (params) => {
                        const icon = getProficiencyIcon(params.data as Node, experienceToFontIconMap);
                        return [params.name, `{icon|${icon}}`].join("\n");
                      },
                      fontFamily: fontFamilies.fontFamilyMulish,
                      fontWeight: 700,
                      fontSize: calculatedRatio * 8 + 12, // From 12px to 20px
                      show: true,
                      rich: {
                        icon: {
                          padding: calculatedRatio * 5 + 5, // from 5px to 10px
                          fontFamily: experienceFontName,
                          fontSize: calculatedRatio * 14 + 6 // from 6 to 20
                        }
                      }
                    }
                  };
                }),
                upperLabel: {
                  show: false
                },
                breadcrumb: {
                  show: false
                },
                levels: [
                  {
                    itemStyle: {
                      borderWidth: 0,
                      gapWidth: 4,
                      borderColor: "transparent"
                    },
                    color: ["#70A9A1", "#40798C", "#1F363D"],
                    colorMappingBy: "value"
                  }
                ]
              }
            ]
          }}
        />
      )}
      {currentPath.length > 1 && (
        <div className={css.skillsDrilldownSkillList}>
          <SkillsTable
            skills={categorySkills as ProfileCustomValue[]}
            profileId={profile.id}
            canEditSkills={canEditSkills}
            rowSelectionState={pick(rowSelectionState, map(categorySkills, "id")) as RowSelectionState}
            setRowSelectionState={setRowSelectionState}
            maxNameLength={maxNameLength}
            allowSkillPreview={allowSkillPreview}
          />
        </div>
      )}
    </>
  );
};
